// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * <xng-change>
 *  Override this handler to add an option that disable sending out RealTimeInventoryRequests
 * </xng-change>
 */
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { addToChainBefore, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    GetProductCollectionApiV2Parameter,
    GetRelatedProductCollectionApiV2Parameter,
} from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import {
    chain,
    LoadRealTimeInventory,
    SetUpProductInfos,
} from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    id: string;
    getProductCollectionParameter: GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter;
    extraProductOptions?: {
        getProductCollectionParameter: GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter;
        numberOfProductsToDisplay: number;
    };
    isShellEdit?: boolean;
    disableInventoryRequest?: boolean; // <xng-change/>
}

interface Props {
    products?: ProductModel[];
    productInfos?: ProductInfo[];
    pricingLoaded?: true;
    errorMessage?: string;
    dataViewHasProducts?: boolean;
}

type HandlerType = Handler<Parameter, Props>;

export const CustomLoadRealTimeInventory: HandlerType = props => {
    if (
        !props.productInfos ||
        props.productInfos.length === 0 ||
        (props.dataViewHasProducts && props.parameter.isShellEdit) ||
        props.parameter.disableInventoryRequest // <xng-change/>
    ) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: props.productInfos.map(o => o.productId),
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "Components/ProductInfoLists/CompleteLoadRealTimeInventory",
                    id: props.parameter.id,
                    realTimeInventory,
                });
            },
            onError: () => {
                props.dispatch({
                    type: "Components/ProductInfoLists/FailedLoadRealTimeInventory",
                    id: props.parameter.id,
                });
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );
};

// MCMC-1834: Handling the variability loader for variant products by setting the value of customerUnitOfMeasure to be null if it's empty string
// TODO: Remove "XngCleanProductInfos" method when do next upgrade, the insite solved the issue in v5.1.2202.464
export const XngCleanProductInfos: HandlerType = props => {
    if (!props.products) {
        return;
    }

    props.products = props.products?.map(product => {
        const newProduct = { ...product, customerUnitOfMeasure: (product.customerUnitOfMeasure || undefined) as any };
        return newProduct;
    });
};

addToChainBefore(chain, SetUpProductInfos, XngCleanProductInfos); // <xng-change/>
replaceInChain(chain, LoadRealTimeInventory, CustomLoadRealTimeInventory); // <xng-change/>
